import useAxios from "axios-hooks";
import moment from "moment";
import { useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { BusinessResDto, UsersResDto } from "../../api/Api";
import { LoadingIndicator } from "react-select/dist/declarations/src/components/indicators";

const BusinessCard = ({ business }: { business: BusinessResDto }) => {
  return (
    <div className="card bg-base-100 shadow-lg border border-base-200 p-6">
      <div className="card-body space-y-4">
        <h2 className="card-title text-2xl font-bold text-primary">
          {business?.businessName}
        </h2>

        {/* Business Info */}
        <div>
          <p className="text-sm text-gray-500">
            Organization ID: {business?.businessId}
          </p>
          <p className="text-sm text-gray-500">
            Location: {business?.address}, {business?.city}, {business?.country}{" "}
            {business?.postalCode}
          </p>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Email</h3>
            <p>{business?.email}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Phone</h3>
            <p>{business?.phoneNumber}</p>
          </div>
        </div>

        {/* Metadata */}
        <div className="text-xs text-gray-400 mt-4">
          <p>Created: {new Date(business?.createdAt).toLocaleString()}</p>
          <p>Updated: {new Date(business?.updatedAt).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default function Organization() {
  const { setTitleComponent } = useOutletContext() as any;

  const [{ data, loading, error }, getBusiness] =
    useAxios<BusinessResDto>(`/businesses/me`);

  const [{ data: students, loading: studentsLoading, error: studentsError }, getStudents] = useAxios<UsersResDto>({
    url: "/users",
    params: {
      roles: ["BUSINESS_MEMBER"],
      page: 1,
      limit: 1,
    },
  });


  useEffect(() => {
    setTitleComponent(
      <div className="flex items-center justify-center gap-2">Organization</div>
    );
  }, []);

  if (loading || studentsLoading) {
    return <h1>Loading</h1>;
  }

  if (error) {
    return (
      <div className="p-4 font-bold text-error">
        {error.response?.data.message}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="pb-10">
        <div className="text-lg font-bold pb-1 opacity-50">
          Organization Info
        </div>
        <BusinessCard business={data as any}></BusinessCard>
      </div>
      <div className="text-lg font-bold pb-1 opacity-50">Subscription Info</div>
      <div className="card bg-base-200 w-full shadow-lg ">
        <div className="w-full">
          <div className="stats shadow w-full">
            <div className="stat">
              <div className="stat-title">Status</div>
              <div
                className={`stat-value text-2xl ${data?.subscription?.status === "active" ? "text-success" : ""
                  } ${data?.subscription?.status === "inactive" ? "text-error" : ""
                  }`}
              >
                {data?.subscription?.status}
              </div>
              <div className="stat-desc">
                {data?.subscription?.status === "active"
                  ? "Subscription is currently active"
                  : "Subscription is not active"}
              </div>
            </div>

            <div className="stat">
              <div className="stat-title">Seats</div>
              <div className="stat-value  flex text-lg">
                <div className="">Total Paid: {data?.subscription?.studentQuantity}</div>
                <div className="divider divider-horizontal"></div>
                <div className="">Used: {students?.totalDocs}</div>
                <div className="divider divider-horizontal"></div>
                <div className="">Remaining: {data?.subscription?.studentQuantity - (students?.totalDocs || 0)}</div>
              </div>
              <div className="stat-desc">The number of paid seats</div>
            </div>

            <div className="stat">
              <div className="stat-title">Valid from</div>
              <div className="stat-value text-2xl">
                {moment(data?.subscription?.startDate).format("MMM D, YYYY")}
              </div>
              <div className="stat-desc">Subscription start date</div>
            </div>

            <div className="stat">
              <div className="stat-title">Valid To</div>
              <div className="stat-value text-2xl">
                {moment(data?.subscription?.endDate).format("MMM D, YYYY")}
              </div>
              <div className="stat-desc">Subscription end date</div>
            </div>

            <div className="stat">
              <div className="stat-title">
                {moment(data?.subscription?.startDate).isAfter(moment())
                  ? "Starts in:"
                  : "Expires in:"}
              </div>
              <div className="stat-value text-2xl">
                {Math.floor(
                  moment
                    .duration(
                      moment(
                        moment(data?.subscription?.startDate).isAfter(moment())
                          ? data?.subscription?.startDate
                          : data?.subscription?.endDate
                      ).diff(moment())
                    )
                    .asDays()
                )}{" "}
                Days
              </div>
              <div className="stat-desc">
                {moment(data?.subscription?.startDate).isAfter(moment())
                  ? "Days until the subscription becomes active"
                  : "Days remaining until expiration"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
